/**
 * UserSettingsStore
 *
 * Persistent user settings stored in localStorage.
 * Handles chart mode and stat grid positions (supports blank spaces).
 */

import type { ThemeMode } from '../config/themes';
import { ThemeManager } from '../rendering/ThemeManager';

// Saved circle position (manually placed by user)
export interface SavedCirclePosition {
  address: string;
  x: number;
  y: number;
}

// Deleted circle info (hidden by user)
export interface DeletedCircleInfo {
  address: string;
  projectName?: string; // Store project name for display in settings
  deletedAt: number; // Timestamp for sorting
}

// Minimized window info (persisted between sessions)
export interface MinimizedWindowInfo {
  id: string; // Unique ID or type (e.g., 'stats', 'settings', 'recruitment', or project address)
  type: 'project' | 'stats' | 'settings' | 'recruitment';
  title: string;
  projectId?: string; // For project modals, the address
}

export interface UserSettings {
  chartMode: 'gas' | 'txCount';
  // Grid-based storage: index -> stat key (allows gaps)
  statGrid: Record<number, string>;
  // Stat tile widths: stat key -> width multiplier (1, 2, or 3)
  statWidths: Record<string, number>;
  // Theme preference: light, dark, or system
  theme: ThemeMode;
  // Saved circle positions (manually placed by user)
  savedCircles: Record<string, SavedCirclePosition>;
  // Deleted circles (hidden by user, can be restored)
  deletedCircles: Record<string, DeletedCircleInfo>;
  // Minimized windows (persisted between sessions)
  minimizedWindows: MinimizedWindowInfo[];
  // Show sparkline mini-charts in stat boxes (beta feature)
  showSparklines: boolean;
}

// Available stat keys for reference
export const STAT_KEYS = ['tps', 'gps', 'mbps', 'bps', 'fbps', 'bebps', 'ubps', 'fps', 'block', 'txs'] as const;
export type StatKey = typeof STAT_KEYS[number];

// Human-readable labels for stats
export const STAT_LABELS: Record<StatKey, string> = {
  tps: 'TX/SEC',
  gps: 'GAS/SEC',
  mbps: 'MINIBLOCKS/SEC',
  bps: 'BLOCKS/SEC',
  fbps: 'FRONTEND BPS',
  bebps: 'BACKEND BPS',
  ubps: 'UPSTREAM BPS',
  fps: 'FPS',
  block: 'BLOCK',
  txs: 'TOTAL TX',
};

// Address-specific stat types
export const ADDRESS_STAT_TYPES = ['tps', 'gps', 'tx60s', 'totalTx', 'totalGas'] as const;
export type AddressStatType = typeof ADDRESS_STAT_TYPES[number];

// Labels for address-specific stats
export const ADDRESS_STAT_LABELS: Record<AddressStatType, string> = {
  tps: 'TX/SEC',
  gps: 'GAS/SEC',
  tx60s: 'TX/60S',
  totalTx: 'TOTAL TX',
  totalGas: 'TOTAL GAS',
};

/**
 * Generate a dynamic stat key for address-specific stats
 * Format: addr:{shortAddress}:{statType}
 */
export function makeAddressStatKey(address: string, statType: AddressStatType): string {
  return `addr:${address}:${statType}`;
}

/**
 * Parse an address stat key back to its components
 * Returns null if not a valid address stat key
 */
export function parseAddressStatKey(key: string): { address: string; statType: AddressStatType } | null {
  if (!key.startsWith('addr:')) return null;
  const parts = key.split(':');
  if (parts.length !== 3) return null;
  const statType = parts[2] as AddressStatType;
  if (!ADDRESS_STAT_TYPES.includes(statType)) return null;
  return { address: parts[1], statType };
}

/**
 * Get the label for any stat key (global or address-specific)
 */
export function getStatLabel(key: string, projectName?: string): string {
  // Check if it's a global stat
  if (STAT_LABELS[key as StatKey]) {
    return STAT_LABELS[key as StatKey];
  }

  // Check if it's an address stat
  const parsed = parseAddressStatKey(key);
  if (parsed) {
    const shortAddr = `${parsed.address.substring(0, 6)}...${parsed.address.substring(parsed.address.length - 4)}`;
    const name = projectName || shortAddr;
    return `${name} ${ADDRESS_STAT_LABELS[parsed.statType]}`;
  }

  return key.toUpperCase();
}

const STORAGE_KEY = 'megaarmy_settings';

const DEFAULT_SETTINGS: UserSettings = {
  chartMode: 'txCount',
  statGrid: {
    0: 'mbps',
    1: 'fps',
    2: 'gps',
    200: 'bps',
    202: 'tps',
    400: 'block',
  },
  statWidths: {
    mbps: 2,
    bps: 2,
    gps: 2,
    block: 2,
    tps: 2,
  },
  theme: 'dark',
  savedCircles: {},
  deletedCircles: {},
  minimizedWindows: [],
  showSparklines: false, // Disabled by default (beta feature)
};

class UserSettingsStore {
  private settings: UserSettings;
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.settings = this.load();
  }

  private load(): UserSettings {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Migration: convert old pinnedStats array to statGrid
        if (parsed.pinnedStats && !parsed.statGrid) {
          const statGrid: Record<number, string> = {};
          parsed.pinnedStats.forEach((key: string, index: number) => {
            statGrid[index] = key;
          });
          parsed.statGrid = statGrid;
          delete parsed.pinnedStats;
        }
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    return { ...DEFAULT_SETTINGS };
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
      this.notifyListeners();
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  // Subscribe to settings changes
  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Chart mode
  get chartMode(): 'gas' | 'txCount' {
    return this.settings.chartMode;
  }

  set chartMode(mode: 'gas' | 'txCount') {
    if (this.settings.chartMode !== mode) {
      this.settings.chartMode = mode;
      this.save();
    }
  }

  // Theme
  get theme(): ThemeMode {
    return this.settings.theme;
  }

  set theme(mode: ThemeMode) {
    if (this.settings.theme !== mode) {
      this.settings.theme = mode;
      this.save();
      this.applyTheme();
    }
  }

  // Sparklines (beta feature)
  get showSparklines(): boolean {
    return this.settings.showSparklines;
  }

  set showSparklines(enabled: boolean) {
    if (this.settings.showSparklines !== enabled) {
      this.settings.showSparklines = enabled;
      this.save();
    }
  }

  /**
   * Apply current theme to DOM and notify PixiJS
   */
  applyTheme(): void {
    const theme = this.settings.theme;

    // Apply to DOM
    if (theme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }

    // Notify PixiJS ThemeManager
    ThemeManager.setTheme(theme);
  }

  /**
   * Initialize theme on app startup
   */
  initializeTheme(): void {
    this.applyTheme();
  }

  // Stat grid
  get statGrid(): Record<number, string> {
    return { ...this.settings.statGrid };
  }

  // Get stat at specific grid index
  getStatAt(index: number): string | null {
    return this.settings.statGrid[index] || null;
  }

  // Set stat at specific grid index
  setStatAt(index: number, key: string): void {
    // Remove stat from any other position first
    this.removeStatFromGrid(key);
    this.settings.statGrid[index] = key;
    this.save();
  }

  // Remove stat from grid entirely
  removeStatFromGrid(key: string): void {
    for (const idx of Object.keys(this.settings.statGrid)) {
      if (this.settings.statGrid[Number(idx)] === key) {
        delete this.settings.statGrid[Number(idx)];
      }
    }
  }

  // Check if a stat is in the grid
  isInGrid(key: string): boolean {
    return Object.values(this.settings.statGrid).includes(key);
  }

  // Remove stat at specific index
  removeStatAt(index: number): void {
    if (this.settings.statGrid[index]) {
      delete this.settings.statGrid[index];
      this.save();
    }
  }

  // Move stat from one index to another
  moveStat(fromIndex: number, toIndex: number): void {
    const stat = this.settings.statGrid[fromIndex];
    if (!stat) return;

    // If target has a stat, swap them
    const targetStat = this.settings.statGrid[toIndex];
    if (targetStat) {
      this.settings.statGrid[fromIndex] = targetStat;
    } else {
      delete this.settings.statGrid[fromIndex];
    }
    this.settings.statGrid[toIndex] = stat;
    this.save();
  }

  // Batch move multiple stats at once (avoids multiple saves/rebuilds)
  batchMoveStats(moves: { fromIndex: number; toIndex: number }[]): void {
    // First, collect all the stats being moved
    const statsToMove: { key: string; toIndex: number }[] = [];
    for (const { fromIndex, toIndex } of moves) {
      const key = this.settings.statGrid[fromIndex];
      if (key) {
        statsToMove.push({ key, toIndex });
        delete this.settings.statGrid[fromIndex];
      }
    }
    // Then place them at their new positions
    for (const { key, toIndex } of statsToMove) {
      this.settings.statGrid[toIndex] = key;
    }
    this.save();
  }

  // Batch update: move a stat to new position and displace others in one operation
  batchUpdateWithDisplacement(
    draggedKey: string,
    fromIndex: number,
    toIndex: number,
    displacements: { fromIndex: number; toIndex: number }[]
  ): void {
    // Remove dragged stat from original position
    delete this.settings.statGrid[fromIndex];

    // Collect displaced stats
    const statsToMove: { key: string; toIndex: number }[] = [];
    for (const { fromIndex: dispFrom, toIndex: dispTo } of displacements) {
      const key = this.settings.statGrid[dispFrom];
      if (key) {
        statsToMove.push({ key, toIndex: dispTo });
        delete this.settings.statGrid[dispFrom];
      }
    }

    // Place displaced stats at new positions
    for (const { key, toIndex: newIdx } of statsToMove) {
      this.settings.statGrid[newIdx] = key;
    }

    // Place dragged stat at target position
    this.settings.statGrid[toIndex] = draggedKey;

    this.save();
  }

  // Batch resize: set width and displace stats in one operation
  batchResizeWithDisplacement(
    key: string,
    width: number,
    displacements: { fromIndex: number; toIndex: number }[]
  ): void {
    // Collect displaced stats
    const statsToMove: { key: string; toIndex: number }[] = [];
    for (const { fromIndex: dispFrom, toIndex: dispTo } of displacements) {
      const dispKey = this.settings.statGrid[dispFrom];
      if (dispKey) {
        statsToMove.push({ key: dispKey, toIndex: dispTo });
        delete this.settings.statGrid[dispFrom];
      }
    }

    // Place displaced stats at new positions
    for (const { key: moveKey, toIndex: newIdx } of statsToMove) {
      this.settings.statGrid[newIdx] = moveKey;
    }

    // Set the width
    const clampedWidth = Math.max(1, Math.min(3, Math.round(width)));
    this.settings.statWidths[key] = clampedWidth;

    this.save();
  }

  // Add stat to first available slot (accounting for wide stats)
  // New stats spawn within first 3 rows only
  addStat(key: string): void {
    if (this.isInGrid(key)) return;

    const GRID_ROWS = 100; // For index calculations (matches StatsRenderer)
    const MAX_SPAWN_ROWS = 3; // New stats only spawn in first 3 rows

    // Helper to convert index to col/row
    const indexToPosition = (index: number) => ({
      col: Math.floor(index / GRID_ROWS),
      row: index % GRID_ROWS,
    });

    // Helper to convert col/row to index
    const positionToIndex = (col: number, row: number) => col * GRID_ROWS + row;

    // Build set of all blocked positions (including those occupied by wide stats)
    const blocked = new Set<number>();
    for (const [indexStr, statKey] of Object.entries(this.settings.statGrid)) {
      const idx = Number(indexStr);
      const width = this.settings.statWidths[statKey] || 1;
      const { col, row } = indexToPosition(idx);
      // Block all positions this stat occupies (same row, multiple columns)
      for (let w = 0; w < width; w++) {
        blocked.add(positionToIndex(col + w, row));
      }
    }

    // Find first unblocked slot within first 3 rows
    const maxCols = 20;
    for (let col = 0; col < maxCols; col++) {
      for (let row = 0; row < MAX_SPAWN_ROWS; row++) {
        const index = positionToIndex(col, row);
        if (!blocked.has(index)) {
          this.settings.statGrid[index] = key;
          this.save();
          return;
        }
      }
    }
  }

  // Get all stats as ordered array (for compatibility)
  get pinnedStats(): string[] {
    const entries = Object.entries(this.settings.statGrid)
      .map(([idx, key]) => ({ index: Number(idx), key }))
      .sort((a, b) => a.index - b.index);
    return entries.map(e => e.key);
  }

  // Legacy compatibility
  isPinned(key: string): boolean {
    return this.isInGrid(key);
  }

  pinStat(key: string): void {
    this.addStat(key);
  }

  unpinStat(key: string): void {
    this.removeStatFromGrid(key);
    // Reset width to default when removing stat
    delete this.settings.statWidths[key];
    this.save();
  }

  togglePin(key: string): void {
    if (this.isInGrid(key)) {
      this.unpinStat(key);
    } else {
      this.pinStat(key);
    }
  }

  // Get max index used in grid
  getMaxIndex(): number {
    const indices = Object.keys(this.settings.statGrid).map(Number);
    return indices.length > 0 ? Math.max(...indices) : -1;
  }

  // Get stat tile width multiplier (1, 2, or 3)
  getStatWidth(key: string): number {
    return this.settings.statWidths[key] || 1;
  }

  // Set stat tile width multiplier (1, 2, or 3)
  setStatWidth(key: string, width: number): void {
    const clampedWidth = Math.max(1, Math.min(3, Math.round(width)));
    if (this.settings.statWidths[key] !== clampedWidth) {
      this.settings.statWidths[key] = clampedWidth;
      this.save();
    }
  }

  // Saved circle positions
  get savedCircles(): Record<string, SavedCirclePosition> {
    return { ...this.settings.savedCircles };
  }

  // Save a circle position
  saveCirclePosition(address: string, x: number, y: number): void {
    const normalizedAddress = address.toLowerCase();
    this.settings.savedCircles[normalizedAddress] = {
      address: normalizedAddress,
      x,
      y
    };
    this.save();
    // console.log(`🔵 Saved circle position for ${normalizedAddress}: (${x}, ${y})`);
  }

  // Remove a saved circle position
  removeSavedCircle(address: string): void {
    const normalizedAddress = address.toLowerCase();
    if (this.settings.savedCircles[normalizedAddress]) {
      delete this.settings.savedCircles[normalizedAddress];
      this.save();
      // console.log(`🔵 Removed saved circle for ${normalizedAddress}`);
    }
  }

  // Check if a circle position is saved
  isCircleSaved(address: string): boolean {
    return !!this.settings.savedCircles[address.toLowerCase()];
  }

  // Get saved circle position
  getSavedCirclePosition(address: string): SavedCirclePosition | null {
    return this.settings.savedCircles[address.toLowerCase()] || null;
  }

  // Deleted circles
  get deletedCircles(): Record<string, DeletedCircleInfo> {
    return { ...this.settings.deletedCircles };
  }

  // Get deleted circles as sorted array (most recent first)
  getDeletedCirclesList(): DeletedCircleInfo[] {
    return Object.values(this.settings.deletedCircles)
      .sort((a, b) => b.deletedAt - a.deletedAt);
  }

  // Delete a circle (hide it from view)
  deleteCircle(address: string, projectName?: string): void {
    const normalizedAddress = address.toLowerCase();
    this.settings.deletedCircles[normalizedAddress] = {
      address: normalizedAddress,
      projectName,
      deletedAt: Date.now()
    };
    // Also remove from saved if it was saved
    if (this.settings.savedCircles[normalizedAddress]) {
      delete this.settings.savedCircles[normalizedAddress];
    }
    this.save();
    // console.log(`🔵 Deleted circle for ${projectName || normalizedAddress}`);
  }

  // Restore a deleted circle
  restoreCircle(address: string): void {
    const normalizedAddress = address.toLowerCase();
    if (this.settings.deletedCircles[normalizedAddress]) {
      delete this.settings.deletedCircles[normalizedAddress];
      this.save();
      // console.log(`🔵 Restored circle for ${normalizedAddress}`);
    }
  }

  // Check if a circle is deleted
  isCircleDeleted(address: string): boolean {
    return !!this.settings.deletedCircles[address.toLowerCase()];
  }

  // Clear all deleted circles (restore all)
  restoreAllCircles(): void {
    this.settings.deletedCircles = {};
    this.save();
    // console.log('🔵 Restored all deleted circles');
  }

  // Minimized windows management
  get minimizedWindows(): MinimizedWindowInfo[] {
    return [...this.settings.minimizedWindows];
  }

  // Add a minimized window
  addMinimizedWindow(window: MinimizedWindowInfo): void {
    // Don't add duplicates
    const exists = this.settings.minimizedWindows.some(w =>
      w.type === window.type && (w.type !== 'project' || w.projectId === window.projectId)
    );
    if (!exists) {
      this.settings.minimizedWindows.push(window);
      this.save();
      // console.log('📋 Minimized window saved:', window.title);
    }
  }

  // Remove a minimized window
  removeMinimizedWindow(type: string, projectId?: string): void {
    const initialLength = this.settings.minimizedWindows.length;
    this.settings.minimizedWindows = this.settings.minimizedWindows.filter(w => {
      if (type === 'project') {
        return !(w.type === 'project' && w.projectId === projectId);
      }
      return w.type !== type;
    });
    if (this.settings.minimizedWindows.length !== initialLength) {
      this.save();
      // console.log('📋 Minimized window removed:', type, projectId || '');
    }
  }

  // Check if a window is minimized
  isWindowMinimized(type: string, projectId?: string): boolean {
    return this.settings.minimizedWindows.some(w => {
      if (type === 'project') {
        return w.type === 'project' && w.projectId === projectId;
      }
      return w.type === type;
    });
  }

  // Get minimized window info
  getMinimizedWindow(type: string, projectId?: string): MinimizedWindowInfo | null {
    return this.settings.minimizedWindows.find(w => {
      if (type === 'project') {
        return w.type === 'project' && w.projectId === projectId;
      }
      return w.type === type;
    }) || null;
  }

  // Clear all minimized windows
  clearMinimizedWindows(): void {
    this.settings.minimizedWindows = [];
    this.save();
    // console.log('📋 Cleared all minimized windows');
  }
}

// Singleton instance
export const userSettings = new UserSettingsStore();
